/**
 * SectionVisual - Container visual for section blocks
 *
 * Renders content within a document section:
 * - Text blocks
 * - Headers
 * - Dividers
 * - Play references
 *
 * Expects children to be embedded in properties.__resolved_children
 * (set by DocumentVisual or API).
 *
 * Pure visual component - no editor state or handlers.
 */

import { BlockRenderer } from '../../BlockRenderer'
import type { BlockVisualProps } from '../../Types'
import type { SectionBlock, ResolvedSectionChild } from '@/types/blocks'

export function SectionVisual({ block }: BlockVisualProps) {
	// Type guard
	if (block.type !== 'section') {
		console.error('SectionVisual called with non-section block:', block.type)
		return null
	}

	const sectionBlock = block as SectionBlock

	// Extract children from properties (embedded by DocumentVisual)
	const children = (sectionBlock.properties as any).__resolved_children as
		| ResolvedSectionChild[]
		| undefined

	// No children state
	if (!children || children.length === 0) {
		return (
			<div className="section-empty" data-block-id={sectionBlock.id}>
				<p style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>
					Section is empty
				</p>
			</div>
		)
	}

	// Render section with header and children
	return (
		<section className="section-container" data-block-id={sectionBlock.id}>
			{/* Section header */}
			<h3
				style={{
					color: sectionBlock.properties.color || 'var(--foreground)',
					marginBottom: '1rem',
				}}
			>
				{sectionBlock.properties.name}
			</h3>

			{/* Render all children recursively */}
			<div className="section-content">
				{children.map((child, index) => {
					// Embed resolved data into block properties for reference blocks
					let childBlock = child.block

					if (child.type === 'play_reference' && 'resolvedBlock' in child) {
						childBlock = {
							...childBlock,
							properties: {
								...childBlock.properties,
								__resolved_block: child.resolvedBlock,
							},
						} as any
					}

					return <BlockRenderer key={childBlock.id || `child-${index}`} block={childBlock} />
				})}
			</div>
		</section>
	)
}
